/**
 * Tree statistics and metrics
 * Functions for calculating counts, completion rates, and other aggregate data
 */

import { TodoTree } from '../../types/todo';

/**
 * Gets the total number of items in the tree
 */
export function getTotalItemCount(tree: TodoTree): number {
  return Object.keys(tree).length;
}

/**
 * Gets the number of completed items in the tree
 */
export function getCompletedItemCount(tree: TodoTree): number {
  return Object.values(tree).filter(item => item.done).length;
}

/**
 * Gets the number of blocked items in the tree
 */
export function getBlockedItemCount(tree: TodoTree): number {
  return Object.values(tree).filter(item => item.blocked).length;
}

/**
 * Gets the number of archived items in the tree
 */
export function getArchivedItemCount(tree: TodoTree): number {
  return Object.values(tree).filter(item => item.archived).length;
}

/**
 * Calculates the completion percentage of the tree
 * @param tree - The todo tree structure
 * @returns Completion percentage (0-100)
 */
export function getCompletionPercentage(tree: TodoTree): number {
  const total = getTotalItemCount(tree);
  if (total === 0) return 0;

  const completed = getCompletedItemCount(tree);
  return Math.round((completed / total) * 100);
}

/**
 * Checks if a node is a leaf (has no children)
 * @param tree - The todo tree structure
 * @param nodeId - The ID of the node to check
 * @returns True if the node is a leaf, false otherwise
 */
export function isLeafNode(tree: TodoTree, nodeId: string): boolean {
  const node = tree[nodeId];
  if (!node) return false;
  return node.children.length === 0;
}

/**
 * Calculates the total time estimate for a subtree
 * Only counts leaf node estimates. Excludes done tasks.
 * Includes archived tasks if they're not done.
 *
 * @param tree - The todo tree structure
 * @param nodeId - The root node ID of the subtree
 * @returns Total time estimate in hours (sum of all descendant leaf estimates)
 */
export function calculateSubtreeTimeEstimate(tree: TodoTree, nodeId: string): number {
  const node = tree[nodeId];
  if (!node) return 0;

  // Exclude done tasks from time estimates
  if (node.done) return 0;

  // If this is a leaf node, return its estimate (or 0 if undefined)
  if (node.children.length === 0) {
    return node.timeEstimate ?? 0;
  }

  // For parent nodes, recursively sum all descendant leaf estimates
  let total = 0;
  for (const childId of node.children) {
    total += calculateSubtreeTimeEstimate(tree, childId);
  }

  return total;
}
